import { debounce } from 'lodash-es'
import { PropType, computed, defineComponent, nextTick, onMounted, onUnmounted, ref } from 'vue'

import { CsTag } from '@sunrise/components'

export default defineComponent({
    name: 'MsDynamicTags',
    components: { CsTag },
    props: {
        tags: {
            type: Array as PropType<string[]>,
            required: true,
        },
        maxRows: {
            type: Number,
            default: 1,
            validator: (value: number) => value >= 1,
        },
    },
    setup(props) {
        const elTagsContainer = ref<HTMLElement | null>(null)
        const indexOfLastTagInMaxRow = ref(-1)
        const tagHeight = 24
        const gapBetweenTags = 8
        const maxHeight = computed(() => {
            return props.maxRows > 1
                ? `${(tagHeight * props.maxRows + gapBetweenTags * props.maxRows).toString()}px`
                : `${tagHeight.toString()}px`
        })
        const debouncedHandleTagDisplay = debounce(handleTagDisplay, 500)
        const shouldHideTag = (index: number): boolean => {
            return indexOfLastTagInMaxRow.value !== -1 && index > indexOfLastTagInMaxRow.value
        }
        let prevCurrentRowCount = 0

        function handleTagDisplay() {
            const tags = elTagsContainer.value?.querySelectorAll(':scope > span') as NodeListOf<HTMLSpanElement>
            const currentRowCount = countCurrentRowsOfTags(tags)

            if (currentRowCount > props.maxRows) {
                indexOfLastTagInMaxRow.value = getIndexOfLastTagInMaxRow(tags, props.maxRows)
            }

            if (prevCurrentRowCount > currentRowCount) {
                indexOfLastTagInMaxRow.value = -1
            }

            prevCurrentRowCount = currentRowCount
        }

        function countCurrentRowsOfTags(tags: NodeListOf<HTMLSpanElement>): number {
            let rowCount = 0
            let lastTopOffset = undefined

            for (const tag of tags) {
                const topOffset = tag.offsetTop
                if (topOffset !== lastTopOffset) {
                    rowCount++
                    lastTopOffset = topOffset
                }
            }

            return rowCount
        }

        function getIndexOfLastTagInMaxRow(tags: NodeListOf<HTMLSpanElement>, rowNumber: number): number {
            let lastTagOfRowIndex = -1
            let lastTopOffset: null | number = null
            let currentRow = 1

            tags.forEach((tag, index) => {
                const topOffset = tag.offsetTop

                if (isNewRowDetected(lastTopOffset, topOffset)) {
                    currentRow++

                    if (currentRow > rowNumber) {
                        return
                    }
                }

                if (currentRow === rowNumber) {
                    lastTagOfRowIndex = index
                }

                lastTopOffset = topOffset
            })

            return lastTagOfRowIndex
        }

        function isNewRowDetected(lastTopOffset: number | null, topOffset: number): boolean {
            return lastTopOffset !== null && topOffset !== lastTopOffset
        }

        onMounted(() => {
            nextTick(() => handleTagDisplay())

            window.addEventListener('resize', debouncedHandleTagDisplay)
        })

        onUnmounted(() => window.removeEventListener('resize', debouncedHandleTagDisplay))

        return { elTagsContainer, indexOfLastTagInMaxRow, maxHeight, shouldHideTag }
    },
})
